import React from "react";
import { useAppSelector } from "../../../services/hooks/useReduxHooks";
import { cn } from "../../../utils/cn";
import {
  EntityImageLookup,
  LoyaltyOfferGraphic,
} from "../../../utils/loyaltyTypes";
import {
  SparkleIcon,
  StarIcon,
} from "../../screens/S-Cart/ItemList/bundleItem/icons";
import { mapLoyaltyOfferIconSlug } from "../LoyaltyOfferIcon/LoyaltyGraphicIcon";
import { PImage } from "../../shared/PImage/PImage";
import { CollageArc } from "./collageArcs";
import {
  CollageArc as CollageArcSpot,
  CollageDecoration,
  CollagePlacement,
  DESIGN_HEIGHT,
  MAX_COLLAGE_ITEMS,
  collageArrangement,
  collageBand,
  collageCutouts,
} from "./offerCollageLayout";
import styles from "./OfferGraphicCollage.module.scss";

type CollageImage = Extract<LoyaltyOfferGraphic, { type: "image" }>;

const isImage = (graphic: LoyaltyOfferGraphic): graphic is CollageImage =>
  graphic.type === "image";

/**
 * The graphics a collage would actually draw, in the order given.
 *
 * Every slot is a ring that takes whatever its graphic is — a photograph, an
 * offer icon, a short piece of text — the same way the badge treatments do
 * outside the collage. Only an `image` whose entity the menu no longer has a
 * photograph for drops out, which is why a caller cannot tell from `graphics`
 * alone whether anything will appear.
 */
export const drawableCollageGraphics = (
  graphics: LoyaltyOfferGraphic[] | undefined,
  entityImageLookup: EntityImageLookup
): LoyaltyOfferGraphic[] =>
  (graphics ?? []).filter(
    (graphic) =>
      !isImage(graphic) ||
      Boolean(entityImageLookup[graphic.entityId]?.itemImage)
  );

/**
 * Whether a collage would draw anything, so a surface can show the offer's
 * icon badge instead of an empty box.
 */
export const useHasCollageGraphics = (
  graphics: LoyaltyOfferGraphic[] | undefined
): boolean => {
  const entityImageLookup = useAppSelector(
    (state) => state.config.entityImageLookup
  );

  return drawableCollageGraphics(graphics, entityImageLookup).length > 0;
};

type OfferGraphicCollageProps = {
  /** The rings to draw, from `useOfferCollageGraphics`. */
  graphics: LoyaltyOfferGraphic[];
  /** Hides the star / sparkle / arc flourishes, e.g. on a dense card. */
  hideDecorations?: boolean;
  className?: string;
};

/**
 * The ring of item images representing a BOGO or bundle offer.
 *
 * Replaces `BundleOfferImages` on loyalty-service surfaces: placement comes
 * from {@link collageArrangement} and the ring is a CSS border, so every
 * arrangement scales with the container. Legacy surfaces keep
 * `BundleOfferImages`.
 */
export const OfferGraphicCollage = ({
  graphics,
  hideDecorations,
  className,
}: OfferGraphicCollageProps) => {
  const entityImageLookup = useAppSelector(
    (state) => state.config.entityImageLookup
  );
  const alternatePercentageIcon = useAppSelector(
    (state) => state.config.restaurantInfo?.config?.alternatePercentageIcon
  );
  const drawable = drawableCollageGraphics(graphics, entityImageLookup);

  const visible = drawable.slice(0, MAX_COLLAGE_ITEMS);
  const arrangement = collageArrangement(visible.length);

  if (!arrangement) {
    return null;
  }

  const { items: placements, decorations } = arrangement;

  /*
   * The slot is as tall as the drawing, not as tall as the board, so a two-up
   * does not sit in half a box of empty space. The canvas is centred on the
   * board's middle, so it also has to shift by however far the band's middle
   * sits from it — 0.5px at two images, 20px at nine.
   */
  const band = collageBand(arrangement);
  const bandOffset = DESIGN_HEIGHT / 2 - (band.top + band.height / 2);

  const boxStyle = {
    "--collage-band-height": band.height,
    "--collage-band-offset": `${bandOffset}px`,
  } as React.CSSProperties;

  const cutouts = collageCutouts(placements);

  /*
   * A hole where a later ring is drawn over this one, so the surface shows
   * through the band rather than the photograph beneath it.
   *
   * The gradient itself lives in the stylesheet, because RTL mirrors the
   * arrangement through `insetInlineStart` and a mask position written here
   * would not mirror with it — the hole would stay on the ring's right while
   * the ring that cut it moved to the left. Only the first hole is drawn: no
   * arrangement on the board overlaps a ring twice (see `collageCutouts`).
   */
  const clearOverlap = (index: number) => {
    const hole = cutouts[index]?.[0];

    if (!hole) {
      return undefined;
    }

    return {
      "--collage-cut-x": `${hole.x}%`,
      "--collage-cut-y": `${hole.y}%`,
      "--collage-cut-r": `${hole.radius}%`,
    } as React.CSSProperties;
  };

  /** An item ring is a circle, so its width alone sizes it. */
  const placeItem = ({ x, y, size }: CollagePlacement) => ({
    insetInlineStart: `${x}%`,
    top: `${y}%`,
    width: `${size}%`,
  });

  const place = ({ x, y, size, height }: CollageDecoration) => ({
    insetInlineStart: `${x}%`,
    top: `${y}%`,
    width: `${size}%`,
    height: `${height}%`,
  });

  /*
   * The arcs are drawn rotated, and rotating a box grows the box it occupies.
   * The board's numbers are the footprint the arc should end up filling, so it
   * is laid out narrower by that growth instead of spilling past it. Its height
   * is left to the artwork: pinning both axes would letterbox the arc inside
   * the box and shrink it again.
   */
  /** The board's own layer bounds; the turn is already in the artwork. */
  const placeArc = ({ x, y, width, height }: CollageArcSpot) => ({
    insetInlineStart: `${x}%`,
    top: `${y}%`,
    width: `${width}%`,
    height: `${height}%`,
  });

  /** One ring, drawn from whatever its graphic is — as the badges do. */
  const renderGraphic = (graphic: LoyaltyOfferGraphic) => {
    if (isImage(graphic)) {
      return (
        <PImage
          src={entityImageLookup[graphic.entityId]?.itemImage}
          alt=""
          fill
          logoFallback={false}
          style={{ objectFit: "cover", objectPosition: "center" }}
          className={styles.image}
        />
      );
    }

    if (graphic.type === "text") {
      return <span className={styles.text}>{graphic.value}</span>;
    }

    return (
      <span className={styles.icon}>
        {mapLoyaltyOfferIconSlug(
          String(graphic.value),
          alternatePercentageIcon
        )}
      </span>
    );
  };

  return (
    <div
      className={cn(styles.collage, className)}
      data-testid="offer-graphic-collage"
      data-count={visible.length}
      style={boxStyle}
    >
      <div className={styles.canvas}>
        {!hideDecorations && (
          <>
            <span
              className={cn(styles.decoration, styles.star)}
              style={place(decorations.star)}
            >
              <StarIcon />
            </span>
            <span
              className={cn(styles.decoration, styles.sparkle)}
              style={place(decorations.sparkle)}
            >
              <SparkleIcon />
            </span>
            {decorations.arcs.map((spot) => (
              <span
                key={spot.shape}
                className={cn(styles.decoration, styles.arc)}
                style={placeArc(spot)}
              >
                <CollageArc shape={spot.shape} />
              </span>
            ))}
          </>
        )}

        {visible.map((graphic, index) => {
          const placement = placements[index];

          return (
            <div
              key={index}
              className={cn(
                styles.slot,
                Boolean(cutouts[index]?.length) && styles.cut
              )}
              style={{ ...placeItem(placement), ...clearOverlap(index) }}
            >
              <span className={styles.graphic}>{renderGraphic(graphic)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
